import { colors } from '../theme/theme';

export interface AssociatedStudio {
  studio: { _id: string; studioName: string } | string;
  source: string;
  firstSeenAt?: string;
}

export type UserPackStatus = 'active' | 'upcoming' | 'expired' | 'none';

export interface AdminUser {
  _id: string;
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
  image?: string;
  isOnline: boolean;
  isMarketable?: boolean;
  createdAt: string;
  updatedAt: string;
  age?: number;
  gender?: string;
  height?: number;
  weight?: number;
  city?: string;
  registrationSource?: string;
  /**
   * A device registered for push on THIS studio's app right now.
   *
   * Proves the app is installed and the member has signed in since per-app
   * tokens shipped. Its ABSENCE proves nothing — tokens clear on logout and
   * older accounts never re-registered — so it is labelled "app active", never
   * "has not downloaded". Studio-scoped responses only.
   */
  hasAppToken?: boolean;
  registeredViaStudio?:
    | {
        _id: string;
        studioName: string;
        whitelabelConfig?: { appSource?: string; appName?: string };
      }
    | string;
  associatedStudios?: AssociatedStudio[];
  followers?: string[];
  followings?: string[];
  packStatus?: UserPackStatus;
  hasUpcomingPack?: boolean;
}

export type QuickTabValue = 'all' | 'marketable' | 'new_week';

export type UserColumnKey =
  | 'phone'
  | 'age'
  | 'gender'
  | 'city'
  | 'platform'
  | 'app'
  | 'marketable'
  | 'status'
  | 'joined'
  | 'studios'
  | 'pack';

export const USER_COLUMN_LABELS: Record<UserColumnKey, string> = {
  phone: 'Phone',
  age: 'Age',
  gender: 'Gender',
  city: 'City',
  platform: 'Platform',
  app: 'App',
  marketable: 'Marketable',
  status: 'Status',
  joined: 'Joined',
  studios: 'Studios',
  pack: 'Pack',
};

export const DEFAULT_USER_COLUMNS: UserColumnKey[] = [
  'phone',
  'age',
  'gender',
  'city',
  'platform',
  // Studio-facing: how a member arrived (app vs the studio's website via the
  // partner API) and whether their app is currently active.
  'app',
  'marketable',
  // 'status' (online/offline presence) is OFF by default for everyone; it can be
  // re-enabled from the Columns menu (admins only — it's a restricted studio column).
  'joined',
  'studios',
  'pack',
];

export const USER_COLUMNS_STORAGE_KEY = 'admin-users-table-columns';

export const PLATFORM_SOURCE_CONFIG: Record<string, { label: string; color: string }> = {
  mobile_app: { label: 'SweatSocial', color: '#6C63FF' },
  website: { label: 'Website', color: '#0288D1' },
  purple_pilates: { label: 'Purple Pilates', color: '#9C27B0' },
  laya_pilates: { label: 'Laya Pilates', color: '#FF6F00' },
  partner_api: { label: 'Partner API', color: '#FF6F00' },
  house_of_pilates: { label: 'House of Pilates', color: '#795548' },
  poise_and_pulse: { label: 'Poise & Pulse', color: '#00838F' },
  ember_and_ice: { label: 'Ember & Ice', color: '#E65100' },
  chakra: { label: 'Chakra Athletica', color: '#1A1A1A' },
  chakra_raipur: { label: 'Chakra Raipur', color: '#1A1A1A' },
  rebel: { label: 'Rebél', color: '#89010A' },
};

/**
 * A studio's booking website registers as `<brand>_web` — `chakra_web` — so
 * the surface is kept without losing the brand. Everywhere that reasons about
 * WHICH studio a member belongs to wants the brand back.
 *
 * Without this the Platform column renders the raw `chakra_web` with no label
 * or colour, and filtering by Chakra silently omits every member who signed up
 * on Chakra's own website.
 */
export function basePlatformSource(source: string): string {
  return source.endsWith('_web') ? source.slice(0, -'_web'.length) : source;
}

/** True when the member arrived through the studio's booking website. */
export function isWebSignup(source: string): boolean {
  return source.endsWith('_web');
}

export interface PlatformOption {
  value: string;
  label: string;
}

// SweatSocial + Website are not studios, so they always bookend the list.
// The whitelabel entries here are only a SEED / offline fallback — the live list is
// built dynamically from studios that have a whitelabel app enabled (see
// buildCreateUserPlatforms), so onboarding a new whitelabel needs no code change.
export const CREATE_USER_PLATFORMS: readonly PlatformOption[] = [
  { value: 'mobile_app', label: 'SweatSocial' },
  { value: 'purple_pilates', label: 'Purple Pilates' },
  { value: 'laya_pilates', label: 'Laya Pilates' },
  { value: 'poise_and_pulse', label: 'Poise & Pulse' },
  { value: 'coreline', label: 'Coreline' },
  { value: 'chakra', label: 'Chakra Athletica' },
  { value: 'chakra_raipur', label: 'Chakra Raipur' },
  { value: 'rebel', label: 'Rebél' },
  { value: 'website', label: 'Website' },
] as const;

interface StudioLike {
  studioName?: string;
  whitelabelConfig?: { appSource?: string; appName?: string };
}

/**
 * Builds the "Platform" options for the Create-user dialog dynamically:
 *   SweatSocial → [every whitelabel app] → Website
 *
 * The middle is seeded from CREATE_USER_PLATFORMS (so known apps show even before
 * studios load / if the fetch fails) and augmented from any studio whose
 * whitelabelConfig.appSource is set. So the moment a studio's whitelabel is
 * enabled, its app appears here automatically — no hardcoding per app.
 * A studio's whitelabelConfig.appName wins as the label when present.
 */
export function buildCreateUserPlatforms(studios: StudioLike[]): PlatformOption[] {
  const labelBy = new Map<string, string>();
  // Seed with known whitelabels (skip the SweatSocial/Website bookends).
  for (const p of CREATE_USER_PLATFORMS) {
    if (p.value === 'mobile_app' || p.value === 'website') continue;
    labelBy.set(p.value, p.label);
  }
  // Augment from studios that have a whitelabel app enabled.
  for (const s of studios || []) {
    const src = s.whitelabelConfig?.appSource?.trim();
    if (!src || src === 'mobile_app' || src === 'website') continue;
    labelBy.set(src, s.whitelabelConfig?.appName?.trim() || labelBy.get(src) || s.studioName || src);
  }
  const whitelabel = Array.from(labelBy, ([value, label]) => ({ value, label })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
  return [
    { value: 'mobile_app', label: 'SweatSocial' },
    ...whitelabel,
    { value: 'website', label: 'Website' },
  ];
}

export function loadVisibleColumns(): UserColumnKey[] {
  try {
    const raw = localStorage.getItem(USER_COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_USER_COLUMNS;
    const parsed = JSON.parse(raw) as UserColumnKey[];
    return parsed.filter(col => col in USER_COLUMN_LABELS);
  } catch {
    return DEFAULT_USER_COLUMNS;
  }
}

export function saveVisibleColumns(columns: UserColumnKey[]) {
  localStorage.setItem(USER_COLUMNS_STORAGE_KEY, JSON.stringify(columns));
}

export function getUserInitials(name?: string | null): string {
  if (!name?.trim()) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

export function getAvatarColor(name?: string | null): string {
  const palette = ['#E91E63', '#6C63FF', '#0288D1', '#9C27B0', '#FF6F00', '#2E7D32', '#00838F'];
  const seed = (name || 'user').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return palette[seed % palette.length];
}

export function userMatchesPackFilter(user: AdminUser, filter: UserPackStatus | null): boolean {
  if (!filter) return true;
  // packStatus collapses to 'active' when a user has both an active and an
  // upcoming pack (active takes display precedence). Filtering by 'upcoming'
  // must still match those users, so it checks hasUpcomingPack instead of
  // the collapsed status — otherwise they'd be misleadingly hidden.
  if (filter === 'upcoming') return !!user.hasUpcomingPack;
  return (user.packStatus ?? 'none') === filter;
}

export function isUserMarketable(user: AdminUser): boolean {
  if (user.isMarketable !== undefined && user.isMarketable !== null) {
    return user.isMarketable;
  }
  const source = user.registrationSource || 'mobile_app';
  // Detach markers are not registration platforms — don't treat as SS.
  if (source.startsWith('removed_by_')) return false;
  return source === 'mobile_app' || source === 'website';
}

export function isProfileComplete(user: AdminUser): boolean {
  return Boolean(user.name && user.email && user.phoneNumber);
}

// Platform column text when registrationSource is a detach marker:
// removed_by_admin | removed_by_user | removed_by_<appSource>
// (e.g. removed_by_purple_pilates → "Removed by Purple Pilates").
export function getRemovedByLabel(user: AdminUser): string | null {
  const source = user.registrationSource;
  if (!source?.startsWith('removed_by_')) return null;
  if (source === 'removed_by_admin') return 'Removed by Admin';
  if (source === 'removed_by_user') return 'Removed by User';
  // Legacy generic value
  if (source === 'removed_by_studio') return 'Removed by Studio';

  const appSource = source.slice('removed_by_'.length);
  const label =
    PLATFORM_SOURCE_CONFIG[appSource]?.label ||
    appSource
      .split('_')
      .filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  return `Removed by ${label}`;
}

export function getPlatformDisplay(user: AdminUser): { label: string; color: string } {
  const raw = user.registrationSource || 'mobile_app';
  if (raw.startsWith('removed_by_')) {
    return { label: getRemovedByLabel(user) || 'Removed', color: colors.warning };
  }
  // `studio_web`: a studio WITHOUT an app of its own (no brand source to
  // name), joined through its booking website or migrated as its member. Show
  // the studio, never the raw "studio · Web" — these are not SweatSocial's.
  if (raw === 'studio_web') {
    const regStudio = user.registeredViaStudio;
    const entry = user.associatedStudios?.find((a) => typeof a.studio === 'object');
    const name =
      typeof regStudio === 'object' && regStudio?.studioName
        ? regStudio.studioName
        : entry && typeof entry.studio === 'object'
          ? entry.studio.studioName
          : null;
    return { label: `${name || 'Studio'} · Web`, color: '#00897B' };
  }
  // `chakra_web` is Chakra — shown with its own colour and a Web suffix, not
  // as an unrecognised source in default grey.
  const source = basePlatformSource(raw);
  const webSuffix = isWebSignup(raw) ? ' · Web' : '';
  const isPartner = source === 'partner_api' || source === 'purple_pilates' || source === 'laya_pilates';

  if (isPartner || source.startsWith('house_') || source === 'poise_and_pulse' || source === 'ember_and_ice' || source === 'chakra' || source === 'chakra_raipur' || source === 'rebel') {
    const regStudio = user.registeredViaStudio;
    const studioEntry = user.associatedStudios?.find(a => typeof a.studio === 'object');
    const studioName =
      typeof regStudio === 'object' && regStudio?.studioName
        ? regStudio.studioName
        : studioEntry && typeof studioEntry.studio === 'object'
          ? studioEntry.studio.studioName
          : null;

    const cfg = PLATFORM_SOURCE_CONFIG[source] || { label: studioName || 'Partner Studio', color: '#FF6F00' };
    const base = studioName && !PLATFORM_SOURCE_CONFIG[source]
      ? { label: studioName, color: cfg.color }
      : cfg;
    return { label: `${base.label}${webSuffix}`, color: base.color };
  }

  const fallback = PLATFORM_SOURCE_CONFIG[source] || { label: source, color: colors.textSecondary };
  return { label: `${fallback.label}${webSuffix}`, color: fallback.color };
}

export function userMatchesPlatform(user: AdminUser, platform: string): boolean {
  if (platform === 'all') return true;
  const source = user.registrationSource || 'mobile_app';
  // Detach markers are not filterable platforms — only match via studio id
  // if they still have associations (they usually won't after remove).
  if (platform.startsWith('studio:')) {
    const studioId = platform.replace('studio:', '');
    const regStudio = user.registeredViaStudio;
    if (typeof regStudio === 'object' && regStudio?._id === studioId) return true;
    return user.associatedStudios?.some(a => {
      const sid = typeof a.studio === 'object' ? a.studio?._id : a.studio;
      return sid === studioId;
    }) ?? false;
  }
  if (source.startsWith('removed_by_')) return false;
  // Filtering by Chakra must include Chakra's website signups.
  return basePlatformSource(source) === basePlatformSource(platform);
}

export function derivePlatformOptions(users: AdminUser[]) {
  const base = [
    { value: 'all', label: 'All', color: colors.textPrimary },
    { value: 'mobile_app', label: 'SweatSocial', color: '#6C63FF' },
    { value: 'website', label: 'Website', color: '#0288D1' },
  ];

  const studioMap = new Map<string, { value: string; label: string; color: string }>();
  users.forEach(user => {
    if (typeof user.registeredViaStudio === 'object' && user.registeredViaStudio?._id) {
      const id = user.registeredViaStudio._id;
      if (!studioMap.has(id)) {
        const source = user.registrationSource || 'partner_api';
        studioMap.set(id, {
          value: `studio:${id}`,
          label: user.registeredViaStudio.studioName,
          color: PLATFORM_SOURCE_CONFIG[source]?.color || '#FF6F00',
        });
      }
    }
    user.associatedStudios?.forEach(a => {
      if (typeof a.studio === 'object' && a.studio?._id && !studioMap.has(a.studio._id)) {
        studioMap.set(a.studio._id, {
          value: `studio:${a.studio._id}`,
          label: a.studio.studioName,
          color: PLATFORM_SOURCE_CONFIG[a.source]?.color || '#FF6F00',
        });
      }
    });
  });

  return [...base, ...Array.from(studioMap.values())];
}

export function deriveCityOptions(users: AdminUser[]): string[] {
  const cities = new Set<string>();
  users.forEach(u => {
    if (u.city?.trim()) cities.add(u.city.trim());
  });
  return Array.from(cities).sort((a, b) => a.localeCompare(b));
}

export function formatJoinedDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function computeUserStats(users: AdminUser[]) {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const total = users.length;
  const complete = users.filter(isProfileComplete).length;
  const newThisMonth = users.filter(u => new Date(u.createdAt) >= startOfMonth).length;
  const newSinceMonthStart = newThisMonth;
  const lastMonthTotal = users.filter(u => {
    const d = new Date(u.createdAt);
    return d >= startOfLastMonth && d <= endOfLastMonth;
  }).length;

  const thisMonthTotal = users.filter(u => new Date(u.createdAt) >= startOfMonth).length;
  const pctVsLastMonth =
    lastMonthTotal === 0
      ? thisMonthTotal > 0 ? 100 : 0
      : ((thisMonthTotal - lastMonthTotal) / lastMonthTotal) * 100;

  return {
    total,
    complete,
    completePct: total === 0 ? 0 : Math.round((complete / total) * 100),
    newThisMonth,
    newSinceMonthStart,
    pctVsLastMonth,
    monthStartLabel: startOfMonth.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
    weekAgo,
  };
}
